/**
 * Get Enquiries API
 * Fetches enquiries (bookings with status=new, in_progress, complete, lost, or deleted) from the Collins API
 */

import {
  BookingsApi,
  Configuration,
  GetAllBookingsRequest,
  GetAllBookingsStatusEnum,
  ViewBooking
} from "collins-js-sdk";
import { dmnIdentity } from "../../utils/dmnIdentity";
import { getEnvironmentProps } from "../../utils/environment";
import { convertAllDatesToUTC } from "../../utils/helpers";
import {
  Enquiry,
  EnquiryNotification,
  EnquiryStatus,
  FacetCounts,
  FilterOptions
} from "../../types/enquiries";
import { VenuePage } from "../../types/venues";
import { errorHandler, apiFetchNotOkHandler } from "../errorHandler";

/**
 * Maps a ViewBooking from the Collins API to an Enquiry
 */
export const mapViewBookingToEnquiry = (
  booking: ViewBooking,
  venues: VenuePage[]
): Enquiry => {
  const venue = venues.find((v) => v._id === booking.venueId);

  const mapStatus = (status?: string): EnquiryStatus => {
    switch (status) {
      case "new":
        return "new";
      case "in_progress":
        return "in_progress";
      case "complete":
        return "complete";
      case "rejected":
        return "cancelled";
      case "lost":
        return "lost";
      case "deleted":
        return "deleted";
      default:
        return "new";
    }
  };

  const formatDate = (date?: Date | null): string | undefined => {
    if (!date) return undefined;
    return date.toISOString().split("T")[0];
  };

  const formatDateTime = (date?: Date | null): string | undefined => {
    if (!date) return undefined;
    return date.toISOString();
  };

  return {
    _id: booking.id ?? "",
    reference: booking.reference?.toString() ?? "",
    status: mapStatus(booking.status),
    date: formatDate(booking.date),
    time: booking.time ?? undefined,
    num_people: booking.numPeople ?? undefined,
    customer: {
      _id: booking.customerId ?? undefined,
      first_name: booking.firstName ?? undefined,
      last_name: booking.lastName ?? undefined,
      email: booking.email ?? undefined,
      phone: booking.phone ?? undefined,
      company: booking.company ?? undefined
    },
    venue: venue
      ? {
          _id: venue._id,
          title: venue.title
        }
      : undefined,
    booking_type: booking.type
      ? {
          id: booking.type.id ?? "",
          name: booking.type.name ?? ""
        }
      : undefined,
    assigned_to: booking.assignedTo ?? undefined,
    created: formatDateTime(booking.createdDate),
    last_updated: formatDateTime(booking.lastUpdated),
    follow_up: formatDate(booking.followUp),
    labels: booking.labels ?? [],
    comments: booking.comments?.map((comment) => ({
      _id: comment.userId,
      text: comment.comment,
      created_at: comment.time,
      user_id: comment.userId,
      user_name: comment.fullName
    })),
    partner_source: booking.partnerSource ?? undefined
  };
};

/**
 * Parameters for fetching paginated enquiries from the Collins API
 */
export interface PaginatedEnquiriesParams {
  venueIds: string[];
  venueFilter?: string[];
  venues: VenuePage[];
  statuses: GetAllBookingsStatusEnum[];
  page: number;
  limit: number;
  sort?: string[];
  assignedTo?: string;
  query?: string;
  bookingTypes?: string[];
  labels?: string[];
  notifications?: string[];
  partnerSource?: string;
  dateFrom?: string;
  dateTo?: string;
}

/**
 * Response from paginated enquiries API call
 */
export interface PaginatedEnquiriesResponse {
  enquiries: Enquiry[];
  total: number;
}

/**
 * Builds the GetAllBookingsRequest by applying optional filter parameters.
 * Extracted to reduce the cognitive complexity of fetchPaginatedEnquiriesFromAPI.
 */
const buildEnquiryRequest = (
  params: PaginatedEnquiriesParams
): GetAllBookingsRequest => {
  const request: GetAllBookingsRequest = {
    status: params.statuses,
    page: params.page.toString(),
    limit: params.limit,
    createdDateFrom: new Date("2000-01-01")
  };

  if (params.sort?.length) {
    request.sort = params.sort;
  }
  if (params.assignedTo) {
    // Support comma-separated user IDs for multi-select filtering.
    // The Collins API (BookingFilters.php:240) handles multiple IDs
    // via explode(',', $id) using whereIn to match any of the specified users.
    request.assignedTo = params.assignedTo.split(",").map((id) => id.trim());
  }
  if (params.query) {
    request.query = params.query;
  }
  if (params.venueFilter?.length) {
    request.venueId = params.venueFilter;
  }
  if (params.bookingTypes?.length) {
    request.type = params.bookingTypes;
  }
  if (params.labels?.length) {
    request.labels = params.labels;
  }
  if (params.notifications?.length) {
    request.notifications = params.notifications;
  }
  if (params.partnerSource) {
    request.partnerSource = params.partnerSource;
  }
  if (params.dateFrom) {
    request.dateFrom = new Date(params.dateFrom);
  }
  if (params.dateTo) {
    request.dateTo = new Date(params.dateTo);
  }

  return request;
};

/**
 * Fetches a single page of enquiries from the Collins API with server-side
 * pagination, filtering, and sorting. This replaces the previous approach of
 * fetching ALL enquiries and filtering/paginating client-side.
 *
 * Similar to collins-ui's getBookingsFromCollinsApi (bookingsManager.js:390-407),
 * this reads the Collins API's pagination headers (X-Pagination-Total-Results,
 * X-Pagination-Page, X-Pagination-Per-Page) to determine the total result count.
 *
 * A very old createdDateFrom is passed to ensure ALL enquiries are included,
 * regardless of their created date. This fixes an issue where in-progress
 * enquiries without a received date or with a received date over a year ago
 * were missing.
 *
 * @param params - Pagination, filter, and sort parameters
 * @returns Object containing the page of enquiries and the total count
 */
export const fetchPaginatedEnquiriesFromAPI = async (
  params: PaginatedEnquiriesParams
): Promise<PaginatedEnquiriesResponse> => {
  const { venueIds, venues } = params;

  if (!venueIds.length) {
    return { enquiries: [], total: 0 };
  }

  const token = await dmnIdentity.getAccessToken();
  const environmentVars = getEnvironmentProps(token);

  const config = new Configuration({
    accessToken: token,
    basePath: environmentVars?.COLLINS_API_BASE_URL,
    queryParamsStringify: (queryParams) => {
      return Object.keys(queryParams)
        .map((key) => {
          const value = queryParams[key as keyof typeof queryParams];
          return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
        })
        .join("&");
    }
  });

  try {
    const request = buildEnquiryRequest(params);

    const rawResponse = await new BookingsApi(config).getAllBookingsRaw(
      convertAllDatesToUTC(request)
    );

    const totalResults = parseInt(
      rawResponse.raw.headers.get("X-Pagination-Total-Results") ?? "0",
      10
    );

    // Clone the response before SDK parsing consumes it.
    // The SDK's ViewBookingTypeFromJSONTyped only maps known fields
    // (id, name, guestlist, privateHire, value) and strips 'color',
    // so we read the raw JSON to preserve booking type colours and
    // notification data (used by the bulk clear notifications feature).
    // If cloning fails, colours fall back to the primary theme color.
    const colourByBookingId = new Map<string, string>();
    const notificationsByBookingId = new Map<string, EnquiryNotification[]>();
    try {
      const rawJson = await rawResponse.raw.clone().json();
      const rawBookings: Array<{
        id?: string;
        type?: { color?: string };
        notifications?: Array<{ type?: string; status?: string }>;
      }> = rawJson?.bookings ?? [];
      for (const raw of rawBookings) {
        if (raw.id && raw.type?.color) {
          colourByBookingId.set(raw.id, raw.type.color);
        }
        if (raw.id && raw.notifications?.length) {
          notificationsByBookingId.set(
            raw.id,
            raw.notifications
              .filter(
                (n): n is { type: string; status: string } =>
                  Boolean(n.type) && Boolean(n.status)
              )
              .map((n) => ({ type: n.type, status: n.status }))
          );
        }
      }
    } catch {
      // Raw data extraction failed; colours use primary theme, notifications empty.
    }

    const parsed = await rawResponse.value();
    const bookings = parsed.bookings ?? [];

    const enquiries = bookings.map((booking: ViewBooking) => {
      const enquiry = mapViewBookingToEnquiry(booking, venues);
      const colour = colourByBookingId.get(booking.id ?? "");
      if (enquiry.booking_type && colour) {
        enquiry.booking_type.color = colour;
      }
      const notifications = notificationsByBookingId.get(booking.id ?? "");
      if (notifications) {
        enquiry.notifications = notifications;
      }
      return enquiry;
    });

    return {
      enquiries,
      total: totalResults
    };
  } catch (error) {
    errorHandler(error, "Error fetching paginated enquiries");
    return { enquiries: [], total: 0 };
  }
};

/**
 * Default facet counts returned when server request fails or returns no data
 */
const DEFAULT_FACET_COUNTS: FacetCounts = {
  totalHeads: 0,
  status: {
    new: 0,
    in_progress: 0,
    complete: 0,
    cancelled: 0,
    lost: 0,
    deleted: 0
  },
  notifications: {
    follow_up: 0,
    waiting_2hrs: 0,
    todays_enquiries: 0,
    new_messages: 0,
    mentioned: 0,
    customer_notes: 0,
    offer_selected: 0,
    deposit_authenticated: 0,
    deposit_paid: 0,
    packages_requested: 0,
    customer_preorder_added: 0,
    customer_preorder_complete: 0,
    email_delivery_failed: 0,
    customer_change: 0,
    contract_signed: 0,
    customer_details: 0
  },
  notifications_inc_rejected: {
    message_received: 0,
    customer_cancelled: 0
  },
  concierge: {
    true: 0,
    false: 0
  }
};

interface ServerFacetCountsResponse {
  totalHeads?: number;
  status?: Record<string, number>;
  notifications?: Record<string, number>;
  notifications_inc_rejected?: Record<string, number>;
  concierge?: Record<string, number>;
}

/**
 * Maps raw server facet count data to the FacetCounts type.
 * The server returns status keys using API naming (e.g. "rejected"),
 * which must be mapped to the client-side naming (e.g. "cancelled").
 */
const mapServerFacetCounts = (
  serverCounts: ServerFacetCountsResponse
): FacetCounts => {
  const statusCounts = serverCounts.status ?? {};
  const notificationCounts = serverCounts.notifications ?? {};
  const rejectedCounts = serverCounts.notifications_inc_rejected;
  const conciergeCounts = serverCounts.concierge ?? {};

  return {
    totalHeads: serverCounts.totalHeads ?? 0,
    status: {
      new: statusCounts.new ?? 0,
      in_progress: statusCounts.in_progress ?? 0,
      complete: statusCounts.complete ?? 0,
      cancelled: statusCounts.rejected ?? 0,
      lost: statusCounts.lost ?? 0,
      deleted: statusCounts.deleted ?? 0
    },
    notifications: {
      follow_up: notificationCounts.follow_up ?? 0,
      waiting_2hrs: notificationCounts.untaken_two_hours ?? 0,
      todays_enquiries: notificationCounts.for_today ?? 0,
      new_messages: notificationCounts.message_received ?? 0,
      mentioned: notificationCounts.mentioned ?? 0,
      customer_notes: notificationCounts.customer_notes ?? 0,
      offer_selected: notificationCounts.offer_selected ?? 0,
      deposit_authenticated: notificationCounts.deposit_authenticated ?? 0,
      deposit_paid: notificationCounts.deposit_paid ?? 0,
      packages_requested: notificationCounts.packages_requested ?? 0,
      customer_preorder_added: notificationCounts.customer_preorder_added ?? 0,
      customer_preorder_complete:
        notificationCounts.customer_preorder_complete ?? 0,
      email_delivery_failed: notificationCounts.email_delivery_failed ?? 0,
      customer_change: notificationCounts.customer_change ?? 0,
      contract_signed: notificationCounts.contract_signed ?? 0,
      customer_details: notificationCounts.customer_details ?? 0
    },
    notifications_inc_rejected: {
      message_received:
        rejectedCounts?.message_received ??
        notificationCounts.message_received ??
        0,
      customer_cancelled:
        rejectedCounts?.customer_cancelled ??
        notificationCounts.customer_cancelled ??
        0
    },
    concierge: {
      true: conciergeCounts.true ?? 0,
      false: conciergeCounts.false ?? 0
    }
  };
};

/**
 * Maps client-side EnquiryStatus values to the Core API status names.
 * The Core API uses 'rejected' where the client uses 'cancelled'.
 */
const mapStatusToCoreApi = (status: string): string =>
  status === "cancelled" ? "rejected" : status;

/**
 * Appends shared filter parameters (venue, booking_type, assigned_to,
 * labels, partner_source, query) to the given URLSearchParams.
 */
const appendFilterParams = (
  params: URLSearchParams,
  filters?: FilterOptions
): void => {
  if (filters?.venue) {
    const venues = Array.isArray(filters.venue)
      ? filters.venue
      : [filters.venue];
    venues.forEach((v) => params.append("venue_id", v));
  }
  if (filters?.booking_type?.length) {
    params.append("type", filters.booking_type.join(","));
  }
  if (filters?.assigned_to) {
    params.append("assigned_to", filters.assigned_to);
  }
  if (filters?.labels) {
    const labels = Array.isArray(filters.labels)
      ? filters.labels
      : [filters.labels];
    labels.forEach((l) => params.append("labels", l));
  }
  if (filters?.partner_source) {
    params.append("partner_source", filters.partner_source);
  }
  if (filters?.query) {
    params.append("query", filters.query);
  }
};

/**
 * Fetches the filtered total number of guests (totalHeads) from the server.
 * Unlike fetchFacetCountsFromServer (which excludes status for sidebar counts),
 * this includes ALL filters (including status) to get an accurate guest count
 * that reflects the current filter selection.
 *
 * @param filters - Current filter options including status
 * @returns The total number of guests matching the current filters
 */
export const fetchFilteredTotalHeadsFromServer = async (
  filters?: FilterOptions
): Promise<number> => {
  try {
    const token = await dmnIdentity.getAccessToken();
    const environmentVars = getEnvironmentProps(token);
    const baseUrl = environmentVars?.CORE_API_BASE_URL;

    if (!baseUrl) {
      errorHandler(
        new Error("CORE_API_BASE_URL is not configured"),
        "Error fetching filtered total heads"
      );
      return 0;
    }

    const params = new URLSearchParams();
    params.append("facets_only", "true");

    const statusValues = filters?.status?.length
      ? filters.status.map(mapStatusToCoreApi)
      : ["new", "in_progress"];
    params.append("status", statusValues.join(","));

    appendFilterParams(params, filters);

    const response = await fetch(`${baseUrl}/bookings?${params.toString()}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });

    if (!response.ok) {
      apiFetchNotOkHandler(response, "Error fetching filtered total heads");
      return 0;
    }

    const data = await response.json();
    return data?.payload?.facetCounts?.totalHeads ?? 0;
  } catch (error) {
    errorHandler(error, "Error fetching filtered total heads");
    return 0;
  }
};

/**
 * Fetches facet counts from the server using the facets_only parameter.
 * Similar to collins-ui's getBookingsCounts (bookingsManager.js:502-526),
 * this calls the /bookings endpoint with facets_only=true to retrieve
 * counts without fetching full booking data.
 *
 * Filters are applied server-side EXCEPT for status, ensuring that
 * status counts remain accurate regardless of the currently selected
 * status filter. This matches the existing client-side behaviour
 * from filterEnquiriesExceptStatus (api.ts:198-216).
 *
 * @param filters - Current filter options (status is excluded from the request)
 * @returns FacetCounts with status, notification, and concierge breakdowns
 */
export const fetchFacetCountsFromServer = async (
  filters?: FilterOptions
): Promise<FacetCounts> => {
  try {
    const token = await dmnIdentity.getAccessToken();
    const environmentVars = getEnvironmentProps(token);
    const baseUrl = environmentVars?.CORE_API_BASE_URL;

    if (!baseUrl) {
      errorHandler(
        new Error("CORE_API_BASE_URL is not configured"),
        "Error fetching facet counts"
      );
      return { ...DEFAULT_FACET_COUNTS };
    }

    const params = new URLSearchParams();
    params.append("status", "new,in_progress,complete,lost,deleted");
    params.append("facets_only", "true");

    appendFilterParams(params, filters);

    const response = await fetch(`${baseUrl}/bookings?${params.toString()}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });

    if (!response.ok) {
      apiFetchNotOkHandler(response, "Error fetching facet counts");
      return { ...DEFAULT_FACET_COUNTS };
    }

    const data = await response.json();
    const facetCounts = data?.payload?.facetCounts;

    if (facetCounts) {
      return mapServerFacetCounts(facetCounts as ServerFacetCountsResponse);
    }

    return { ...DEFAULT_FACET_COUNTS };
  } catch (error) {
    errorHandler(error, "Error fetching facet counts");
    return { ...DEFAULT_FACET_COUNTS };
  }
};
